import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAccess from "../../hooks/useAccess.jsx";
import { invalidatePageDataCacheEntries, writePageDataCacheEntry } from "../../hooks/usePageData.js";
import { invalidateShellDataCache } from "../../hooks/useShellData.js";
import { ACCESS_PERMISSION_KEYS } from "../../lib/accessControl.js";
import { beginPlanningMutationActivity } from "../../utils/planningMutationActivity.js";
import { dispatchPlanningStateChange } from "../../utils/planningCrossViewSync.js";
import { syncResultsAfterPlanningMutation } from "../../utils/resultsWorkspaceData.js";
import { useToast } from "../../components/ResultsPrimitives.jsx";
import {
  applyPullForward,
  buildPullForwardSelections,
  cancelPullForwardAnalysis,
  fetchLatestPullForwardResults,
  fetchPullForwardAnalysis,
  normalizePullForwardDispatchPeriod,
  previewPullForward,
  startPullForwardAnalysis,
} from "./pullForwardApi.js";

const RESULTS_LATEST_API_URL = "/api/results/latest";
const PULL_FORWARD_INVALIDATED_API_URLS = [
  "/api/dashboard",
  "/api/dashboard/home",
  "/api/data-manager/state",
];

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

const ANALYSIS_POLL_INTERVAL_MS = 1_000;
const ANALYSIS_MAX_CONSECUTIVE_POLL_FAILURES = 3;
const ANALYSIS_CANCELLATION_BARRIER_TIMEOUT_MS = 30_000;
const ANALYSIS_TERMINAL_STATUSES = new Set(["completed", "cancelled", "failed"]);
const ANALYSIS_ACTIVE_STATUSES = new Set(["running", "cancelling"]);

function normalizeAnalysisStatus(value, fallback = "idle") {
  const status = normalizeText(value).toLowerCase();
  return ["running", "cancelling", "completed", "cancelled", "failed"].includes(status)
    ? status
    : fallback;
}

function normalizeProgressState(value, fallback = "pending") {
  const progressState = normalizeText(value).toLowerCase();
	if (["compatible", "incompatible"].includes(progressState)) {
	  return "completed";
	}
  return ["pending", "running", "completed", "compatible", "incompatible", "error"].includes(progressState)
    ? progressState
    : fallback;
}

const READY_REASON_CODES = new Set([
  "ready",
  "compatible",
  "candidate_ready",
  "target_ready",
  "dompeur_sequential_ready",
]);

const CAPACITY_REASON_CODES = new Set([
  "capacity_insufficient",
  "capacity_to_free",
  "compartment_capacity_insufficient",
  "current_capacity_insufficient",
]);

const ANALYSIS_ERROR_REASON_CODES = new Set([
  "analysis_error",
  "candidate_runtime_unavailable",
  "candidate_workbook_unavailable",
  "effective_payload_mismatch",
  "preview_replay_unavailable",
  "runtime_revision_unstable",
]);

function normalizedReasonCode(value) {
  return normalizeText(value?.reason_code || value?.reasonCode).toLowerCase();
}

function normalizedStatusLabel(value) {
  return normalizeText(value?.status_label || value?.statusLabel)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function isReadyBusinessStatus(value) {
  const status = normalizeText(value?.status).toLowerCase();
  const statusLabel = normalizedStatusLabel(value);
  const reasonCode = normalizedReasonCode(value);
  if (ANALYSIS_ERROR_REASON_CODES.has(reasonCode) || reasonCode === "analysis_pending") {
    return false;
  }
  if (READY_REASON_CODES.has(reasonCode)) {
    return true;
  }
  if (CAPACITY_REASON_CODES.has(reasonCode)) {
    return false;
  }
  if (statusLabel) {
    return ["pret", "pret a previsualiser", "ready"].includes(statusLabel);
  }
  // Unknown codes remain conservative and retain the legacy status contract.
  return status === "ready";
}

function nestedAnalysisSnapshot(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  return [payload.snapshot, payload.initial_snapshot, payload.candidate_snapshot]
    .find((snapshot) => snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) || null;
}

function mergeAnalysisPayload(previousPayload, nextPayload) {
  const previous = previousPayload && typeof previousPayload === "object" ? previousPayload : {};
  const payload = nextPayload && typeof nextPayload === "object" ? nextPayload : {};
  const snapshot = nestedAnalysisSnapshot(payload);
  const merged = {
    ...previous,
    ...(snapshot || {}),
    ...payload,
  };
  if (!Array.isArray(payload.blocks) && Array.isArray(snapshot?.blocks)) {
    merged.blocks = snapshot.blocks;
  }
  if (!Array.isArray(merged.blocks)) {
    merged.blocks = Array.isArray(previous.blocks) ? previous.blocks : [];
  }
  if (merged.status) {
    merged.status = normalizeAnalysisStatus(merged.status, "running");
  }
  if (merged.status === "completed" && merged.analysis_complete == null) {
    merged.analysis_complete = true;
  }
  if (merged.status === "cancelled" || merged.status === "failed") {
    merged.analysis_complete = false;
  }
  merged.active_block_ids = Array.isArray(merged.active_block_ids)
    ? merged.active_block_ids.map(normalizeText).filter(Boolean)
    : [];
  return merged;
}

function analysisFailureMessage(payload) {
  const status = normalizeAnalysisStatus(payload?.status, "");
  if (status !== "failed") {
    return "";
  }
  return normalizeText(
    payload?.error
      || payload?.error_message
      || payload?.message,
  );
}

function recordHasAnalysisError(record) {
  if (!record || typeof record !== "object") {
    return false;
  }
  const progressState = normalizeText(record.progress_state).toLowerCase();
  const reasonCode = normalizedReasonCode(record);
  const statusLabel = normalizedStatusLabel(record);
  return progressState === "error"
    || normalizeText(record.status).toLowerCase() === "error"
    || statusLabel === "erreur"
    || ANALYSIS_ERROR_REASON_CODES.has(reasonCode);
}

function analysisPayloadErrorMessage(payload) {
  const blocks = Array.isArray(payload?.blocks) ? payload.blocks : [];
  for (const block of blocks) {
    if (recordHasAnalysisError(block)) {
      return normalizeText(block.reason_label || block.reason_detail)
        || "Une erreur technique a empêché de conclure l’analyse d’un bloc.";
    }
    const targets = Array.isArray(block?.target_options) ? block.target_options : [];
    const erroredTarget = targets.find(recordHasAnalysisError);
    if (erroredTarget) {
      return normalizeText(erroredTarget.reason_label || erroredTarget.reason_detail)
        || "Une erreur technique a empêché de conclure l’analyse d’une cible.";
    }
  }
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const erroredItem = items.find(recordHasAnalysisError);
  return erroredItem
    ? normalizeText(erroredItem.reason_label || erroredItem.reason_detail)
      || "Une erreur technique a empêché de conclure la prévisualisation."
    : "";
}

function previewStatusFields(item) {
  if (!item || typeof item !== "object") {
    return {};
  }
  return Object.fromEntries(
    ["status", "status_label", "statusLabel", "reason_code", "reasonCode", "reason_label", "reason_detail", "progress_state"]
      .filter((key) => Object.prototype.hasOwnProperty.call(item, key))
      .map((key) => [key, item[key]]),
  );
}

function mergePreviewStatusesIntoCandidateResponse(candidatePayload, previewPayload) {
  const previous = candidatePayload && typeof candidatePayload === "object" ? candidatePayload : null;
  const items = Array.isArray(previewPayload?.items) ? previewPayload.items : [];
  if (!previous || !items.length || !Array.isArray(previous.blocks)) {
    return { payload: previous, invalidatedSelection: false };
  }
  const itemByBlockId = new Map(
    items
      .filter((item) => normalizeText(item?.block_id))
      .map((item) => [normalizeText(item.block_id), item]),
  );
  let invalidatedSelection = false;
  let changed = false;
  const blocks = previous.blocks.map((block) => {
    const item = itemByBlockId.get(normalizeText(block?.block_id));
    if (!item) {
      return block;
    }
    const itemFields = previewStatusFields(item);
    let blockChanged = Object.keys(itemFields).length > 0;
    const targetOptions = Array.isArray(block?.target_options)
      ? block.target_options.map((target) => {
        if (
          normalizeText(target?.target_truck_id) !== normalizeText(item?.target_truck_id)
          || normalizeText(target?.target_trip_id) !== normalizeText(item?.target_trip_id)
        ) {
          return target;
        }
        blockChanged = true;
        changed = true;
        return { ...target, ...itemFields };
      })
      : block?.target_options;
    const readyTarget = Array.isArray(targetOptions)
      ? targetOptions.find((target) => isReadyBusinessStatus(target)
        && ["completed", "compatible"].includes(normalizeProgressState(target?.progress_state || target?.status)))
      : null;
    const blockFields = readyTarget ? previewStatusFields(readyTarget) : itemFields;
    const nextBlock = {
      ...block,
      ...blockFields,
      ...(Array.isArray(targetOptions) ? { target_options: targetOptions } : {}),
    };
    if (!isReadyBusinessStatus(item)) {
      invalidatedSelection = true;
    }
    if (blockChanged) {
      changed = true;
      return nextBlock;
    }
    return block;
  });
  return {
    invalidatedSelection,
    payload: changed ? { ...previous, blocks } : previous,
  };
}

function isAbortError(error) {
  return error?.name === "AbortError";
}

function waitForAnalysisPoll(signal, delayMs = ANALYSIS_POLL_INTERVAL_MS) {
  if (signal?.aborted) {
    return Promise.reject(Object.assign(new Error("Analyse annulée"), { name: "AbortError" }));
  }
  return new Promise((resolve, reject) => {
    let timeoutId = null;
    const cleanup = () => {
      if (timeoutId != null) {
        globalThis.clearTimeout(timeoutId);
      }
      signal?.removeEventListener("abort", abort);
    };
    const complete = () => {
      cleanup();
      resolve();
    };
    const abort = () => {
      cleanup();
      reject(Object.assign(new Error("Analyse annulée"), { name: "AbortError" }));
    };
    timeoutId = globalThis.setTimeout(complete, delayMs);
    signal?.addEventListener("abort", abort, { once: true });
  });
}

function createIdempotencyKey() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `pull-forward-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function isPullForwardBlockSelectable(block) {
  const progressState = normalizeProgressState(block?.progress_state, "");
  return isReadyBusinessStatus(block)
    && (!progressState || progressState === "completed" || progressState === "compatible");
}

function readyTargetOptions(block) {
  return (Array.isArray(block?.target_options) ? block.target_options : [])
    .filter((target) => {
      const legacyReady = normalizeText(target?.status).toLowerCase() === "ready";
      return isReadyBusinessStatus(target) || (!target?.status_label && !target?.statusLabel && !normalizedReasonCode(target) && legacyReady);
    });
}

function isPullForwardSelectionStillValid(block, selection) {
  if (!isPullForwardBlockSelectable(block)) {
    return false;
  }
  const targetTruckId = normalizeText(selection?.targetTruckId || selection?.target_truck_id);
  const targetTripId = normalizeText(selection?.targetTripId || selection?.target_trip_id);
  if (!targetTruckId || !targetTripId) {
    // Keep an explicitly selected block while the dispatcher chooses among
    // multiple ready targets; canPreview still rejects this incomplete choice.
    return true;
  }
  return readyTargetOptions(block).some((target) => (
    normalizeText(target?.target_truck_id) === targetTruckId
    && normalizeText(target?.target_trip_id) === targetTripId
  ));
}

function revalidatePullForwardSelections(blocks, selectionByBlock) {
  const blockById = new Map(
    (Array.isArray(blocks) ? blocks : [])
      .map((block) => [normalizeText(block?.block_id), block])
      .filter(([blockId]) => Boolean(blockId)),
  );
  return Object.fromEntries(
    Object.entries(selectionByBlock && typeof selectionByBlock === "object" ? selectionByBlock : {})
      .filter(([blockId, selection]) => (
        isPullForwardSelectionStillValid(blockById.get(blockId), selection)
      )),
  );
}

function selectionMapsEqual(left, right) {
  const leftEntries = Object.entries(left || {});
  const rightEntries = Object.entries(right || {});
  if (leftEntries.length !== rightEntries.length) {
    return false;
  }
  return leftEntries.every(([blockId, selection]) => {
    const other = right?.[blockId];
    return normalizeText(selection?.targetTruckId || selection?.target_truck_id)
      === normalizeText(other?.targetTruckId || other?.target_truck_id)
      && normalizeText(selection?.targetTripId || selection?.target_trip_id)
        === normalizeText(other?.targetTripId || other?.target_trip_id);
  });
}

export function calculatePullForwardSelectionTotals(blocks, selectionByBlock) {
  const blockById = new Map(
    (Array.isArray(blocks) ? blocks : [])
      .map((block) => [normalizeText(block?.block_id), block])
      .filter(([blockId]) => Boolean(blockId)),
  );
  const selectedBlockIds = Object.keys(
    selectionByBlock && typeof selectionByBlock === "object" ? selectionByBlock : {},
  ).filter((blockId) => blockById.has(blockId));
  return {
    blockCount: selectedBlockIds.length,
    orderCount: selectedBlockIds.reduce(
      (total, blockId) => total + Math.max(0, Number(blockById.get(blockId)?.order_count) || 0),
      0,
    ),
  };
}

function readScope(workspaceStore, initialResultsData) {
  const resultPayload = workspaceStore?.getState?.()?.resultsData || initialResultsData || null;
  return {
    dispatchDate: normalizeText(
      resultPayload?.dispatch_date || resultPayload?.planning_summary?.dispatch_date,
    ),
    dispatchPeriod: normalizePullForwardDispatchPeriod(
      resultPayload?.ui_dispatch_period || resultPayload?.dispatch_period,
    ),
  };
}

function buildScopedResultsLatestUrl(dispatchDate, dispatchPeriod) {
  const query = new URLSearchParams({
    dispatch_date: normalizeText(dispatchDate),
    dispatch_period: normalizePullForwardDispatchPeriod(dispatchPeriod),
  });
  return `${RESULTS_LATEST_API_URL}?${query.toString()}`;
}

export default function usePullForwardController({
  initialResultsData = null,
  renderCurrentSelection = null,
  syncWorkspaceUi = null,
  workspaceStore = null,
} = {}) {
  const { hasPermission, isReady: isAccessReady } = useAccess();
  const { showToast } = useToast();
  const canManage = isAccessReady
    && hasPermission(ACCESS_PERMISSION_KEYS.dispatchPullForwardManage);
  const [isOpen, setIsOpen] = useState(false);
  const [scope, setScope] = useState(() => readScope(workspaceStore, initialResultsData));
  const [candidateResponse, setCandidateResponse] = useState(null);
  const [selectionByBlock, setSelectionByBlock] = useState({});
  const [previewResponse, setPreviewResponse] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const candidateRequestEpochRef = useRef(0);
  const previewRequestEpochRef = useRef(0);
  const idempotencyKeyRef = useRef("");
  const analysisAbortControllerRef = useRef(null);
  const analysisPayloadRef = useRef(null);
  const analysisJobIdRef = useRef("");
  const analysisStatusRef = useRef("idle");
  const isOpenRef = useRef(false);
  const analysisCancellationRequestedJobIdsRef = useRef(new Set());
  const analysisCancellationPromiseRef = useRef(null);
  const preservePreviewOnInvalidSelectionRef = useRef(false);

  const blocks = Array.isArray(candidateResponse?.blocks) ? candidateResponse.blocks : [];
  const analysisStatus = normalizeAnalysisStatus(
    candidateResponse?.status,
    analysisStatusRef.current,
  );
  const analysisJobId = normalizeText(candidateResponse?.job_id || analysisJobIdRef.current);
  const completedBlocks = Math.max(0, Number(candidateResponse?.completed_blocks) || 0);
  const totalBlocks = Math.max(
    completedBlocks,
    Number(candidateResponse?.total_blocks) || blocks.length,
  );
  const reportedActiveBlockIds = Array.isArray(candidateResponse?.active_block_ids)
    ? candidateResponse.active_block_ids.map(normalizeText).filter(Boolean)
    : [];
  const activeBlockIds = (reportedActiveBlockIds.length
    ? reportedActiveBlockIds
    : blocks
      .filter((block) => normalizeProgressState(block?.progress_state, "") === "running")
      .map((block) => normalizeText(block?.block_id))
      .filter(Boolean)
  ).slice(0, 2);

  const limits = {
    maxBlocks: Math.max(1, Number(candidateResponse?.limits?.max_blocks) || 9),
    maxOrders: Math.max(1, Number(candidateResponse?.limits?.max_orders) || 121),
  };
  const selectionTotals = useMemo(
    () => calculatePullForwardSelectionTotals(blocks, selectionByBlock),
    [blocks, selectionByBlock],
  );
  const selections = useMemo(
    () => buildPullForwardSelections(selectionByBlock),
    [selectionByBlock],
  );
  const revalidatedSelectionByBlock = useMemo(
    () => revalidatePullForwardSelections(blocks, selectionByBlock),
    [blocks, selectionByBlock],
  );
  const hasInvalidSelection = !selectionMapsEqual(selectionByBlock, revalidatedSelectionByBlock);
  const hasIncompleteTargetSelection = selectionTotals.blockCount !== selections.length;
  const isBusy = isLoadingCandidates || isPreviewing || isApplying;
  const isMutating = isPreviewing || isApplying;
  const analysisComplete = candidateResponse?.analysis_complete === true;
  const canPreview = Boolean(
    analysisComplete
    && normalizeText(candidateResponse?.candidate_revision)
    && selections.length > 0
    && !hasInvalidSelection
    && !hasIncompleteTargetSelection
    && !isBusy,
  );
  const canApply = Boolean(
    previewResponse?.can_apply === true
    && normalizeText(previewResponse?.preview_revision)
    && selections.length > 0
    && !hasInvalidSelection
    && !hasIncompleteTargetSelection
    && !isBusy,
  );

  const clearPreview = useCallback(() => {
    previewRequestEpochRef.current += 1;
    preservePreviewOnInvalidSelectionRef.current = false;
    setIsPreviewing(false);
    setPreviewResponse(null);
    idempotencyKeyRef.current = "";
  }, []);

  useEffect(() => {
    if (!hasInvalidSelection) {
      return;
    }
    const preservePreview = preservePreviewOnInvalidSelectionRef.current;
    preservePreviewOnInvalidSelectionRef.current = false;
    setSelectionByBlock(revalidatedSelectionByBlock);
    if (!preservePreview) {
      clearPreview();
    }
  }, [clearPreview, hasInvalidSelection, revalidatedSelectionByBlock]);

  const commitAnalysisPayload = useCallback((payload, requestEpoch, fallbackJobId = "") => {
    if (candidateRequestEpochRef.current !== requestEpoch) {
      return null;
    }
    const mergedPayload = mergeAnalysisPayload(analysisPayloadRef.current, payload);
    if (!normalizeText(mergedPayload.job_id) && fallbackJobId) {
      mergedPayload.job_id = fallbackJobId;
    }
    if (!mergedPayload.status) {
      mergedPayload.status = fallbackJobId ? "running" : "completed";
    }
    analysisPayloadRef.current = mergedPayload;
    analysisJobIdRef.current = normalizeText(mergedPayload.job_id || fallbackJobId);
    analysisStatusRef.current = normalizeAnalysisStatus(mergedPayload.status, "running");
    setCandidateResponse(mergedPayload);
    setIsLoadingCandidates(ANALYSIS_ACTIVE_STATUSES.has(analysisStatusRef.current));
    const failureMessage = analysisFailureMessage(mergedPayload) || analysisPayloadErrorMessage(mergedPayload);
    if (failureMessage) {
      setErrorMessage(failureMessage);
    }
    return mergedPayload;
  }, []);

  const stopAnalysis = useCallback(() => {
    analysisAbortControllerRef.current?.abort();
    analysisAbortControllerRef.current = null;
  }, []);

  const requestAnalysisCancellation = useCallback((
    requestedJobId = analysisJobIdRef.current,
    requestedStatus = analysisStatusRef.current,
  ) => {
    const jobId = normalizeText(requestedJobId);
    if (!jobId || !ANALYSIS_ACTIVE_STATUSES.has(requestedStatus)) {
      return null;
    }
    const existingCancellation = analysisCancellationPromiseRef.current;
    if (existingCancellation?.jobId === jobId) {
      return existingCancellation.promise;
    }
    if (analysisCancellationRequestedJobIdsRef.current.has(jobId)) {
      return null;
    }
    analysisCancellationRequestedJobIdsRef.current.add(jobId);
    analysisStatusRef.current = "cancelling";
    const promise = cancelPullForwardAnalysis(jobId)
      .catch(() => null)
      .finally(() => {
        if (analysisCancellationPromiseRef.current?.jobId === jobId) {
          analysisCancellationPromiseRef.current = null;
        }
      });
    analysisCancellationPromiseRef.current = { jobId, promise };
    return promise;
  }, []);

  const waitForAnalysisCancellationBarrier = useCallback(async () => {
    const pendingCancellation = analysisCancellationPromiseRef.current?.promise;
    if (!pendingCancellation) {
      return;
    }
    await new Promise((resolve) => {
      let timeoutId = globalThis.setTimeout(resolve, ANALYSIS_CANCELLATION_BARRIER_TIMEOUT_MS);
      const finish = () => {
        if (timeoutId != null) {
          globalThis.clearTimeout(timeoutId);
          timeoutId = null;
        }
        resolve();
      };
      Promise.resolve(pendingCancellation).then(finish, finish);
    });
  }, []);

  const pollAnalysis = useCallback(async (jobId, requestEpoch, abortController) => {
    let consecutiveFailures = 0;
    while (
      !abortController.signal.aborted
      && candidateRequestEpochRef.current === requestEpoch
      && isOpenRef.current
    ) {
      try {
        await waitForAnalysisPoll(abortController.signal);
        if (
          abortController.signal.aborted
          || candidateRequestEpochRef.current !== requestEpoch
          || !isOpenRef.current
        ) {
          return null;
        }
        const payload = await fetchPullForwardAnalysis(jobId, {
          signal: abortController.signal,
        });
        consecutiveFailures = 0;
        const mergedPayload = commitAnalysisPayload(payload, requestEpoch, jobId);
        if (!mergedPayload) {
          return null;
        }
        const status = normalizeAnalysisStatus(mergedPayload.status, "running");
        if (ANALYSIS_TERMINAL_STATUSES.has(status)) {
          if (status === "failed") {
            setErrorMessage(
              analysisFailureMessage(mergedPayload)
              || "L’analyse pull-forward a échoué; veuillez relancer l’analyse.",
            );
          }
          return mergedPayload;
        }
      } catch (error) {
        if (
          isAbortError(error)
          || abortController.signal.aborted
          || candidateRequestEpochRef.current !== requestEpoch
          || !isOpenRef.current
        ) {
          return null;
        }
        consecutiveFailures += 1;
        if (consecutiveFailures >= ANALYSIS_MAX_CONSECUTIVE_POLL_FAILURES) {
          const activeJobId = analysisJobIdRef.current;
          const activeJobStatus = analysisStatusRef.current;
          void requestAnalysisCancellation(activeJobId, activeJobStatus);
          analysisStatusRef.current = "failed";
          setIsLoadingCandidates(false);
          setErrorMessage(error?.message || "Le suivi de l’analyse a échoué.");
          setCandidateResponse((current) => mergeAnalysisPayload(current, {
            status: "failed",
            analysis_complete: false,
          }));
          return null;
        }
        try {
          await waitForAnalysisPoll(
            abortController.signal,
            ANALYSIS_POLL_INTERVAL_MS * (consecutiveFailures + 1),
          );
        } catch (waitError) {
          if (isAbortError(waitError)) {
            return null;
          }
          throw waitError;
        }
      }
    }
    return null;
  }, [commitAnalysisPayload, requestAnalysisCancellation]);

  const loadCandidates = useCallback(async (requestedScope = null) => {
    const nextScope = requestedScope || readScope(workspaceStore, initialResultsData);
    if (!nextScope.dispatchDate) {
      setErrorMessage("Aucune date de dispatch active n’est disponible.");
      return null;
    }
    const requestEpoch = candidateRequestEpochRef.current + 1;
    candidateRequestEpochRef.current = requestEpoch;
    const previousJobId = analysisJobIdRef.current;
    const previousJobStatus = analysisStatusRef.current;
    void requestAnalysisCancellation(previousJobId, previousJobStatus);
    stopAnalysis();
    await waitForAnalysisCancellationBarrier();
    if (candidateRequestEpochRef.current !== requestEpoch || !isOpenRef.current) {
      return null;
    }
    analysisPayloadRef.current = null;
    analysisJobIdRef.current = "";
    analysisStatusRef.current = "running";
    setScope(nextScope);
    setIsLoadingCandidates(true);
    setErrorMessage("");
    setCandidateResponse(null);
    setSelectionByBlock({});
    clearPreview();
    try {
      const abortController = new AbortController();
      analysisAbortControllerRef.current = abortController;
      const payload = await startPullForwardAnalysis({
        ...nextScope,
        signal: abortController.signal,
      });
      const initialPayload = commitAnalysisPayload(payload, requestEpoch);
      const jobId = normalizeText(initialPayload?.job_id);
      if (!initialPayload || candidateRequestEpochRef.current !== requestEpoch) {
        return null;
      }
      if (!jobId) {
        // A completed snapshot without a job id remains compatible with the
        // previous endpoint contract while the new job API is rolled out.
        analysisStatusRef.current = "completed";
        setIsLoadingCandidates(false);
        const completedPayload = mergeAnalysisPayload(initialPayload, {
          status: "completed",
          analysis_complete: initialPayload.analysis_complete !== false,
        });
        analysisPayloadRef.current = completedPayload;
        setCandidateResponse(completedPayload);
        return completedPayload;
      }
      const status = normalizeAnalysisStatus(initialPayload.status, "running");
      if (!ANALYSIS_TERMINAL_STATUSES.has(status)) {
        void pollAnalysis(jobId, requestEpoch, abortController).finally(() => {
          if (analysisAbortControllerRef.current === abortController) {
            analysisAbortControllerRef.current = null;
          }
        });
      } else {
        setIsLoadingCandidates(false);
      }
      return initialPayload;
    } catch (error) {
      if (candidateRequestEpochRef.current === requestEpoch && !isAbortError(error)) {
        analysisStatusRef.current = "failed";
        setIsLoadingCandidates(false);
        setErrorMessage(error?.message || "Impossible d’analyser les commandes de J+1 et J+2.");
      }
      return null;
    } finally {
      if (
        candidateRequestEpochRef.current === requestEpoch
        && !analysisJobIdRef.current
      ) {
        setIsLoadingCandidates(false);
      }
    }
  }, [
    clearPreview,
    commitAnalysisPayload,
    initialResultsData,
    pollAnalysis,
    requestAnalysisCancellation,
    stopAnalysis,
    waitForAnalysisCancellationBarrier,
    workspaceStore,
  ]);

  const open = useCallback(() => {
    if (!canManage) {
      return;
    }
    const nextScope = readScope(workspaceStore, initialResultsData);
    isOpenRef.current = true;
    setIsOpen(true);
    void loadCandidates(nextScope);
  }, [canManage, initialResultsData, loadCandidates, workspaceStore]);

  const close = useCallback(() => {
    if (isApplying) {
      return;
    }
    const jobId = analysisJobIdRef.current;
    const status = analysisStatusRef.current;
    candidateRequestEpochRef.current += 1;
    isOpenRef.current = false;
    void requestAnalysisCancellation(jobId, status);
    stopAnalysis();
    setIsLoadingCandidates(false);
    clearPreview();
    setIsOpen(false);
  }, [clearPreview, isApplying, requestAnalysisCancellation, stopAnalysis]);

  useEffect(() => {
    if (canManage || !isOpen) {
      return;
    }
    const jobId = analysisJobIdRef.current;
    const status = analysisStatusRef.current;
    candidateRequestEpochRef.current += 1;
    isOpenRef.current = false;
    void requestAnalysisCancellation(jobId, status);
    stopAnalysis();
    setIsLoadingCandidates(false);
    setIsOpen(false);
  }, [canManage, isOpen, requestAnalysisCancellation, stopAnalysis]);

  useEffect(() => () => {
    const jobId = analysisJobIdRef.current;
    const status = analysisStatusRef.current;
    isOpenRef.current = false;
    candidateRequestEpochRef.current += 1;
    void requestAnalysisCancellation(jobId, status);
    stopAnalysis();
  }, [requestAnalysisCancellation, stopAnalysis]);

  const toggleBlock = useCallback((blockId, selected) => {
    const safeBlockId = normalizeText(blockId);
    const block = blocks.find((item) => normalizeText(item?.block_id) === safeBlockId);
    if (!safeBlockId || !block || !isPullForwardBlockSelectable(block)) {
      return;
    }
    const isAlreadySelected = Object.prototype.hasOwnProperty.call(selectionByBlock, safeBlockId);
    if (!selected) {
      if (isAlreadySelected) {
        const nextSelection = { ...selectionByBlock };
        delete nextSelection[safeBlockId];
        setSelectionByBlock(nextSelection);
      }
      setErrorMessage("");
      clearPreview();
      return;
    }
    if (isAlreadySelected) {
      return;
    }
    const nextTotals = calculatePullForwardSelectionTotals(blocks, {
      ...selectionByBlock,
      [safeBlockId]: {},
    });
    if (nextTotals.blockCount > limits.maxBlocks || nextTotals.orderCount > limits.maxOrders) {
      setErrorMessage(
        `La sélection est limitée à ${limits.maxBlocks} blocs et ${limits.maxOrders} commandes.`,
      );
      return;
    }
    const targets = readyTargetOptions(block);
    const onlyTarget = targets.length === 1 ? targets[0] : null;
    setSelectionByBlock({
      ...selectionByBlock,
      [safeBlockId]: {
        targetTruckId: normalizeText(onlyTarget?.target_truck_id),
        targetTripId: normalizeText(onlyTarget?.target_trip_id),
      },
    });
    setErrorMessage("");
    clearPreview();
  }, [blocks, clearPreview, limits.maxBlocks, limits.maxOrders, selectionByBlock]);

  const selectTarget = useCallback((blockId, targetKey) => {
    const safeBlockId = normalizeText(blockId);
    const block = blocks.find((item) => normalizeText(item?.block_id) === safeBlockId);
    const target = readyTargetOptions(block).find((option) => (
      `${normalizeText(option?.target_truck_id)}::${normalizeText(option?.target_trip_id)}` === targetKey
    ));
    if (!safeBlockId || !block || !target) {
      return;
    }
    setSelectionByBlock((currentSelection) => ({
      ...currentSelection,
      [safeBlockId]: {
        targetTruckId: normalizeText(target.target_truck_id),
        targetTripId: normalizeText(target.target_trip_id),
      },
    }));
    setErrorMessage("");
    clearPreview();
  }, [blocks, clearPreview]);

  const preview = useCallback(async () => {
    if (!canPreview) {
      return null;
    }
    setIsPreviewing(true);
    setErrorMessage("");
    const requestEpoch = previewRequestEpochRef.current + 1;
    previewRequestEpochRef.current = requestEpoch;
    try {
      const previewPayload = {
        dispatch_date: scope.dispatchDate,
        dispatch_period: scope.dispatchPeriod,
        candidate_revision: candidateResponse.candidate_revision,
        selections,
      };
      const payload = await previewPullForward(previewPayload);
      if (previewRequestEpochRef.current !== requestEpoch) {
        return null;
      }
      setPreviewResponse(payload);
      const previewStatusSync = mergePreviewStatusesIntoCandidateResponse(candidateResponse, payload);
      if (previewStatusSync.payload && previewStatusSync.payload !== candidateResponse) {
        analysisPayloadRef.current = previewStatusSync.payload;
        preservePreviewOnInvalidSelectionRef.current = previewStatusSync.invalidatedSelection;
        setCandidateResponse(previewStatusSync.payload);
      }
      const previewError = analysisPayloadErrorMessage(payload);
      if (previewError) {
        setErrorMessage(previewError);
      }
      idempotencyKeyRef.current = createIdempotencyKey();
      return payload;
    } catch (error) {
      if (previewRequestEpochRef.current === requestEpoch) {
        setPreviewResponse(null);
        setErrorMessage(error?.message || "Impossible de prévisualiser le regroupement.");
      }
      return null;
    } finally {
      if (previewRequestEpochRef.current === requestEpoch) {
        setIsPreviewing(false);
      }
    }
  }, [canPreview, candidateResponse, scope.dispatchDate, scope.dispatchPeriod, selections]);

  const apply = useCallback(async () => {
    if (!canApply) {
      return null;
    }
    const finishPlanningMutationActivity = beginPlanningMutationActivity();
    setIsApplying(true);
    setErrorMessage("");
    try {
      const payload = await applyPullForward({
        dispatch_date: scope.dispatchDate,
        dispatch_period: scope.dispatchPeriod,
        candidate_revision: candidateResponse.candidate_revision,
        preview_revision: previewResponse.preview_revision,
        idempotency_key: idempotencyKeyRef.current || createIdempotencyKey(),
        selections,
      });

      invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL, ...PULL_FORWARD_INVALIDATED_API_URLS]);
      invalidateShellDataCache();

      const affectedDispatchDates = Array.isArray(payload?.affected_dispatch_dates)
        ? payload.affected_dispatch_dates.map(normalizeText).filter(Boolean)
        : [];
      dispatchPlanningStateChange("dispatch", {
        actionName: "pull-forward:apply",
        affectedDispatchDates,
        currentRuntimeRevision: normalizeText(payload?.current_runtime_revision),
        dispatchDate: scope.dispatchDate,
        dispatchPeriod: scope.dispatchPeriod,
        skipLocalSourceChangesRefresh: true,
      });
      showToast({
        duration: 5500,
        message: normalizeText(payload?.message) || "Les commandes sélectionnées ont été regroupées.",
        title: "Regroupement appliqué",
        type: "success",
      });
      isOpenRef.current = false;
      stopAnalysis();
      setIsOpen(false);

      void fetchLatestPullForwardResults(scope)
        .then((resultPayload) => {
          const state = workspaceStore?.getState?.();
          if (state) {
            const { nextAssignment, nextTrip } = syncResultsAfterPlanningMutation(state, resultPayload, {});
            renderCurrentSelection?.(nextAssignment, nextTrip);
            syncWorkspaceUi?.();
          }
          writePageDataCacheEntry(
            buildScopedResultsLatestUrl(scope.dispatchDate, scope.dispatchPeriod),
            resultPayload,
          );
        })
        .catch((refreshError) => {
          showToast({
            duration: 8000,
            message: refreshError?.message || "Le dispatch sera rafraîchi à la prochaine lecture.",
            title: "Regroupement appliqué — rafraîchissement différé",
            type: "warning",
          });
        });
      return payload;
    } catch (error) {
      setErrorMessage(error?.message || "Impossible d’appliquer le regroupement.");
      return null;
    } finally {
      setIsApplying(false);
      finishPlanningMutationActivity();
    }
  }, [
    canApply,
    candidateResponse,
    previewResponse,
    renderCurrentSelection,
    scope,
    selections,
    showToast,
    stopAnalysis,
    syncWorkspaceUi,
    workspaceStore,
  ]);

  return {
    activeBlockIds,
    analysisComplete,
    analysisJobId,
    analysisStatus,
    apply,
    blocks,
    canApply,
    canManage,
    canPreview,
    candidateResponse,
    close,
    errorMessage,
    hasInvalidSelection,
    hasIncompleteTargetSelection,
    isApplying,
    isBusy,
    isMutating,
    isLoadingCandidates,
    isOpen,
    isPreviewing,
    limits,
    loadCandidates: () => loadCandidates(readScope(workspaceStore, initialResultsData)),
    open,
    preview,
    previewResponse,
    scope,
    selectTarget,
    selectionByBlock,
    selectionTotals,
    completedBlocks,
    totalBlocks,
    toggleBlock,
  };
}
